import styled, { css, keyframes } from 'styled-components';

import {
  HERO_FONT_DISPLAY,
  LandingHero,
} from 'constants/landing-hero';

const MOBILE = '@media (max-width: 760px)';

const isHero = (tone: LandingV2.HeaderTone) => tone === 'heroDark';

const sheen = keyframes`
  0% { transform: translateX(-120%) skewX(-20deg); }
  60%, 100% { transform: translateX(260%) skewX(-20deg); }
`;

export const Shell = styled.header<{ $tone: LandingV2.HeaderTone }>`
  ${({ $tone }) =>
    isHero($tone)
      ? css`
          position: relative;
          z-index: 2;
          width: 100%;
          max-width: 128rem;
          margin: 0 auto;
          padding: 2.4rem 2.4rem 0;
        `
      : css`
          display: block;
          width: 100%;
        `}
`;

export const Bar = styled.nav<{ $tone: LandingV2.HeaderTone }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.6rem;
  flex-wrap: wrap;

  ${({ theme, $tone }) =>
    isHero($tone)
      ? css`
          padding: 1rem 1rem 1rem 1.8rem;
          border-radius: 2.2rem;
          background: ${LandingHero.navGlassBg};
          border: 0.1rem solid ${LandingHero.navGlassBorder};
          box-shadow:
            0 2rem 5rem -2rem ${LandingHero.navGlassShadow},
            inset 0 0.1rem 0 ${LandingHero.navInsetHighlight};
          backdrop-filter: blur(1.8rem);
          -webkit-backdrop-filter: blur(1.8rem);
        `
      : css`
          gap: 3.2rem;
          padding: 2.4rem 7.2rem;
          border-bottom: 0.1rem solid ${theme.colors.border};
        `}

  ${MOBILE} {
    padding: 1.6rem 2.4rem;
    gap: 1.6rem;
  }
`;

export const LogoGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  cursor: pointer;
`;

export const LogoMark = styled.div<{ $tone: LandingV2.HeaderTone }>`
  width: 4rem;
  height: 4rem;
  border-radius: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 900;

  ${({ theme, $tone }) =>
    isHero($tone)
      ? css`
          background: ${LandingHero.logoGradient};
          color: ${LandingHero.logoText};
          font-family: ${HERO_FONT_DISPLAY};
          box-shadow:
            0 0.8rem 2.4rem -0.6rem ${LandingHero.logoShadow},
            inset 0 0.1rem 0 ${LandingHero.logoInsetHighlight};
        `
      : css`
          background: ${theme.colors.accent};
          color: ${theme.colors.overlayText};
        `}
`;

export const Wordmark = styled.div<{ $tone: LandingV2.HeaderTone }>`
  font-size: 2.2rem;
  font-weight: ${({ $tone }) => (isHero($tone) ? 700 : 800)};
  letter-spacing: -0.03rem;
  color: ${({ theme, $tone }) =>
    isHero($tone) ? LandingHero.textPrimary : theme.colors.ink};
  ${({ $tone }) =>
    isHero($tone) &&
    css`
      font-family: ${HERO_FONT_DISPLAY};
    `}
`;

export const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

export const ChangePersonaLink = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.steelGrey};
  cursor: pointer;
`;

export const TabSwitch = styled.div<{ $tone: LandingV2.HeaderTone }>`
  display: flex;
  gap: ${({ $tone }) => (isHero($tone) ? '0.4rem' : '0.6rem')};
  border-radius: ${({ $tone }) => (isHero($tone) ? '1.4rem' : '999rem')};
  padding: ${({ $tone }) => (isHero($tone) ? '0.4rem' : '0.5rem')};

  ${({ theme, $tone }) =>
    isHero($tone)
      ? css`
          background: ${LandingHero.navPillBg};
          border: 0.1rem solid ${LandingHero.navPillBorder};
        `
      : css`
          background: ${theme.colors.surface};
        `}

  ${MOBILE} {
    display: ${({ $tone }) => (isHero($tone) ? 'none' : 'flex')};
  }
`;

export const Tab = styled.button<{ $tone: LandingV2.HeaderTone; $active: boolean }>`
  font-size: ${({ $tone }) => (isHero($tone) ? '1.5rem' : '1.4rem')};
  font-weight: ${({ $tone }) => (isHero($tone) ? 600 : 800)};
  padding: ${({ $tone }) => (isHero($tone) ? '1.1rem 1.8rem' : '1rem 2rem')};
  border-radius: ${({ $tone }) => (isHero($tone) ? '1rem' : '999rem')};
  border: none;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.25s ease, background-color 0.25s ease;

  ${({ theme, $tone, $active }) =>
    isHero($tone)
      ? css`
          background: transparent;
          color: ${LandingHero.navLink};

          &:hover {
            color: ${LandingHero.navLinkHover};
            background: ${LandingHero.navLinkHoverBg};
          }
        `
      : css`
          background: ${$active ? theme.colors.white : 'transparent'};
          color: ${$active ? theme.colors.ink : theme.colors.steelGrey};
        `}
`;

export const CtaButton = styled.button<{ $tone: LandingV2.HeaderTone }>`
  font-weight: 800;
  border: none;
  cursor: pointer;
  font-family: inherit;

  ${({ theme, $tone }) =>
    isHero($tone)
      ? css`
          position: relative;
          overflow: hidden;
          font-size: 1.5rem;
          padding: 1.2rem 2.4rem;
          border-radius: 1.4rem;
          background: ${LandingHero.ctaGradient};
          color: ${LandingHero.ctaText};
          box-shadow:
            0 1rem 2.8rem -1rem ${LandingHero.ctaShadow},
            inset 0 0.1rem 0 ${LandingHero.ctaInsetHighlight};
          transition: transform 0.25s ease, box-shadow 0.25s ease;

          &::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 40%;
            height: 100%;
            background: linear-gradient(
              90deg,
              transparent,
              ${LandingHero.ctaSheen},
              transparent
            );
            animation: ${sheen} 3.6s ease-in-out infinite;
          }

          &:hover {
            transform: translateY(-0.2rem);
            box-shadow:
              0 1.4rem 3.4rem -0.8rem ${LandingHero.ctaShadowHover},
              inset 0 0.1rem 0 ${LandingHero.ctaInsetHighlight};
          }

          @media (prefers-reduced-motion: reduce) {
            &::after {
              animation: none;
            }
          }
        `
      : css`
          font-size: 1.6rem;
          padding: 1.4rem 2.6rem;
          border-radius: 999rem;
          background: ${theme.colors.greenCta};
          color: ${theme.colors.overlayText};
        `}
`;

export const LoginButton = styled.button<{ $tone: LandingV2.HeaderTone }>`
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;

  ${({ theme, $tone }) =>
    isHero($tone)
      ? css`
          font-size: 1.5rem;
          font-weight: 700;
          padding: 1.2rem 2.2rem;
          border-radius: 1.4rem;
          background: transparent;
          color: ${LandingHero.textBody};
          border: 0.1rem solid ${LandingHero.ghostBorder};
          transition:
            color 0.25s ease,
            border-color 0.25s ease,
            background-color 0.25s ease,
            transform 0.25s ease;

          &:hover {
            color: ${LandingHero.textPrimary};
            border-color: ${LandingHero.ghostBorderHover};
            background: ${LandingHero.ghostBgHover};
            transform: translateY(-0.1rem);
          }
        `
      : css`
          font-size: 1.6rem;
          font-weight: 700;
          padding: 1.4rem 2rem;
          border-radius: 999rem;
          background: transparent;
          color: ${theme.colors.darkSlate};
          border: 0.1rem solid ${theme.colors.border};
        `}
`;
